import { useDeleteAttraction } from '@/api/attractions';
import type { Attraction } from '@/config/objects/attractions';
import {
  Badge,
  Button,
  ConfirmDialog,
  EntityDetailShell,
  StatusPill,
  toast,
} from '@startupquickstart/frontend';
import {
  Activity,
  BadgeCheck,
  Globe,
  Images,
  Map,
  MapPin,
  Palette,
  Pencil,
  ShieldAlert,
  Tags,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Persistent location-verification status pill for the attraction hero.
 * Visible from every tab so reviewers can tell at a glance whether the
 * coordinates have been externally / manually confirmed without having
 * to scroll down to the Coordinates side card.
 *
 * The label is intentionally source-agnostic — it just says "Verified"
 * or "Unverified". The API doesn't expose which source produced the
 * verification.
 */
function LocationStatusBadge({
  attraction,
}: {
  attraction: Pick<Attraction, 'isAddressVerified' | 'latitude' | 'longitude'>;
}) {
  const verified = !!attraction.isAddressVerified;
  const hasCoords = attraction.latitude != null && attraction.longitude != null;

  if (verified) {
    return (
      <Badge
        variant="outline"
        className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"
        title="Coordinates have been confirmed against an external source."
      >
        <BadgeCheck className="h-3 w-3" />
        Location verified
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="gap-1 border-amber-200 bg-amber-50 text-amber-800"
      title={
        hasCoords
          ? 'Coordinates exist but have not been confirmed. Open the Coordinates section to Verify.'
          : 'No coordinates set. Open the Coordinates section to add them.'
      }
    >
      <ShieldAlert className="h-3 w-3" />
      {hasCoords ? 'Location unverified' : 'Location missing'}
    </Badge>
  );
}

const tabs = [
  { label: 'Overview', path: 'overview', icon: MapPin },
  { label: 'Types', path: 'types', icon: Tags },
  { label: 'Tours', path: 'tours', icon: Map },
  { label: 'Themes', path: 'themes', icon: Palette },
  { label: 'Translations', path: 'translations', icon: Globe },
  { label: 'Gallery', path: 'gallery', icon: Images },
  { label: 'Health', path: 'health', icon: Activity },
] as const;

export function AttractionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Attraction | null>(null);
  const basePath = `/attractions/${id}`;

  const deleteMutation = useDeleteAttraction();

  const handleDelete = () => {
    if (!pendingDelete) return;
    const promise = deleteMutation.mutateAsync(pendingDelete.id).then(() => {
      navigate('/attractions');
    });
    toast.promise(promise, {
      loading: 'Deleting attraction...',
      success: 'Attraction deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction',
    });
  };

  return (
    <>
      <EntityDetailShell<Attraction>
        resource="attractions"
        queryKey="Attraction"
        id={id}
        basePath={basePath}
        entityLabel="Attraction"
        tabs={tabs}
        contextKey="attraction"
        hero={{
          category: { label: 'Attraction', icon: MapPin },
          title: (a) => a?.name || 'Attraction',
          imageUrl: (a) => a?.featuredImage?.url ?? null,
          fallbackIcon: MapPin,
          imageAlt: (a) => a?.name || 'Attraction',
          meta: (a) => (
            <>
              {a.greaterArea && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {a.greaterArea.name}
                </span>
              )}
              {a.neighborhood && <span>{a.neighborhood.name}</span>}
              {(a.city || a.state) && (
                <span>{[a.city, a.state].filter(Boolean).join(', ')}</span>
              )}
            </>
          ),
          badges: (a) => (
            <div className="flex flex-wrap items-center gap-2">
              {a.status && <StatusPill status={a.status} />}
              <LocationStatusBadge attraction={a} />
            </div>
          ),
        }}
        breadcrumbs={(a) => [
          { label: 'Home', href: '/' },
          { label: 'Attractions', href: '/attractions' },
          { label: a?.name || 'Attraction', current: true },
        ]}
        headerActions={(a) =>
          a ? (
            <>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setPendingDelete(a);
                  setShowDeleteConfirm(true);
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          ) : null
        }
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Attraction"
        description={`Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
